import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import {
  SG_FETCH_SURVEY_QUESTION,
  SG_EDIT_SURVEY_QUESTION,
} from "constants/actions";

import ReactJson from "react-json-view";

import { format } from "date-fns";

import { SURVEY_TYPE, STATUS } from "data/AdminData";

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "components/ui/card";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import { Checkbox } from "components/ui/checkbox";
import { Badge } from "components/ui/badge";
import { cn } from "lib/utils";
// Dropdown menu removed in favor of a left-column actions card

import {
  ArrowLeft,
  BarChart3,
  ClipboardList,
  Eye,
  FileCode,
  MessageCircle,
  PanelsTopLeft,
  Pencil,
  Plus,
  Shuffle,
  SlidersHorizontal,
  Workflow,
} from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "components/ui/accordion";

import MultiSelect from "components/forms/MultiSelect";

import AddEditComment from "./AddEditComment";
import CustomIntroScreensModal from "./CustomIntroScreensModal";
import CustomEndScreensModal from "./CustomEndScreensModal";
import AnalyticsManagerModal from "./AnalyticsManagerModal";
import QuestionStructureManagerModal from "./QuestionStructureManagerModal";
import OutcomeStructureManagerModal from "./OutcomeStructureManagerModal";
import OutcomeFeedbackStructureManagerModal from "./OutcomeFeedbackStructureManagerModal";
import QuestionOrderManager, { applyOrderAndImportance } from "./QuestionOrderManager";
import { ColorPicker as SurveyHeaderColorPicker } from "../categoryQuestions/Components/ColorPicker";

const arrange_Questions = (questions) => {
  // console.log(questions)
  if (questions?.dimension) {
    return questions.dimension
      .map((item, i) => {
        return item.factors.map((factor, f) => {
          return factor.questions.map((q, idx) => {
            return {
              question: q.q,
              dimension: i,
              factor: f,
              q: idx,
              role: q.role,
              reverse: q.reverse,
              order: q.id,
            };
          });
        });
      })
      .flat()
      .flat()
      .sort((a, b) => a.order - b.order);
  }
};

const formatTimestamp = (value) => {
  return value ? format(new Date(value), "dd-MMM-yyyy") : "—";
};

const resolveSurveyTypeLabel = (value) => {
  if (value === undefined || value === null) return "—";
  return (
    SURVEY_TYPE.find((item) => String(item.value) === String(value))?.label ??
    value
  );
};

const resolveStatusLabel = (value) => {
  if (value === undefined || value === null) return "—";
  return STATUS.find((item) => String(item.value) === String(value))?.label ?? value;
};

const badgeTones = {
  success: "border-emerald-200 bg-emerald-100 text-emerald-700",
  warning: "border-amber-200 bg-amber-100 text-amber-700",
  info: "border-sky-200 bg-sky-100 text-sky-700",
};

const InfoItem = ({ label, value, isMonospace, badgeVariant }) => {
  const content =
    value === undefined || value === null || value === ""
      ? "—"
      : value;

  const badgeClass = badgeVariant ? badgeTones[badgeVariant] : undefined;

  return (
    <div className="space-y-1 rounded-md bg-muted/10 p-3">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd
        className={cn(
          "mt-2 text-sm font-medium text-foreground",
          isMonospace && "font-mono text-xs"
        )}
      >
        {badgeClass ? (
          <Badge className={cn("px-2 py-0.5", badgeClass)}>{content}</Badge>
        ) : (
          content
        )}
      </dd>
    </div>
  );
};

const Tile = ({ title, description, children, className }) => (
  <div className={cn("space-y-3 rounded-md bg-muted/10 p-4", className)}>
    <div className="space-y-1">
      <h4 className="text-sm font-semibold text-foreground">{title}</h4>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

const LabelField = ({ label, children }) => (
  <label className="block space-y-2">
    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </span>
    <div>{children}</div>
  </label>
);

const CheckboxRow = ({ label, description, checked, onCheckedChange }) => (
  <div className="flex items-start gap-3">
    <Checkbox
      checked={checked}
      onCheckedChange={(val) => onCheckedChange(!!val)}
      className="mt-0.5"
    />
    <div>
      <p className="text-sm font-medium text-foreground">{label}</p>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  </div>
);

const FollowUpField = ({
  label,
  enabled,
  score,
  onToggle,
  onScoreChange,
}) => (
  <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-background p-4">
    <div className="flex items-center gap-2">
      <Checkbox
        checked={enabled}
        onCheckedChange={(val) => onToggle(!!val)}
      />
      <span className="text-sm font-medium text-foreground">{label}</span>
    </div>
    <Input
      type="number"
      min={0}
      max={10}
      value={score}
      disabled={!enabled}
      onChange={(event) => {
        const raw = Number(event.target.value);
        if (Number.isNaN(raw)) return;
        const clamped = Math.max(0, Math.min(10, raw));
        onScoreChange(clamped);
      }}
      className="w-24"
    />
  </div>
);

const Details = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [questionId, setQuestionId] = useState(false);
  const [questionDetails, setQuestionDetails] = useState(null);
  const [surveyQuestion, setSurveyQuestion] = useState([]);
  const [askComments, setAskComments] = useState([]);
  const [openSummary, setOpenSummary] = useState(false);
  const [openComment, setOpenComment] = useState(false);
  const [openCreateNew, setOpenCreateNew] = useState(false);
  const [openIntroScreens, setOpenIntroScreens] = useState(false);
  const [openAnalyticsManager, setOpenAnalyticsManager] = useState(false);
  const [openOrderManager, setOpenOrderManager] = useState(false);
  const [openStructureManager, setOpenStructureManager] = useState(false);
  const [openOutcomeStructureManager, setOpenOutcomeStructureManager] = useState(false);
  const [openOutcomeFeedbackStructureManager, setOpenOutcomeFeedbackStructureManager] = useState(false);
  const [openEndScreens, setOpenEndScreens] = useState(false);
  const [reorderItems, setReorderItems] = useState([]);
  const [validationSummary, setValidationSummary] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [jsonDraft, setJsonDraft] = useState("");
  const [parsedJson, setParsedJson] = useState(null);
  const [updated, setUpdated] = useState(false);
  const [accordionValue, setAccordionValue] = useState([]);
  const [showOverview, setShowOverview] = useState(false);
  const [advAllowSkip, setAdvAllowSkip] = useState(false);
  const [advAllowSkipComments, setAdvAllowSkipComments] = useState(false);
  const [advLanguages, setAdvLanguages] = useState([]);
  const [advUseLikert, setAdvUseLikert] = useState(null);
  const [advQuestionTitles, setAdvQuestionTitles] = useState(false);
  const [advCategoryTitles, setAdvCategoryTitles] = useState({ EN: "" });
  const [advCustomThanks, setAdvCustomThanks] = useState({ text: "" });
  const [advCustomCategoryIntro, setAdvCustomCategoryIntro] = useState({ text: "" });
  const [advCustomCategoryIntroTitle, setAdvCustomCategoryIntroTitle] = useState({ EN: "" });
  const [advRemoveCategoryThanks, setAdvRemoveCategoryThanks] = useState(false);
  const [advHighFollowUpEnabled, setAdvHighFollowUpEnabled] = useState(false);
  const [advHighFollowUpScore, setAdvHighFollowUpScore] = useState(10);
  const [advLowFollowUpEnabled, setAdvLowFollowUpEnabled] = useState(false);
  const [advLowFollowUpScore, setAdvLowFollowUpScore] = useState(0);
  const [advDemographicsAtEnd, setAdvDemographicsAtEnd] = useState(false);
  const [advDisableEngagementAI, setAdvDisableEngagementAI] = useState(false);

  // Qualifier Options state - with enable/disable toggles
  const [qualOrganizationEnabled, setQualOrganizationEnabled] = useState(false);
  const [qualOrganization, setQualOrganization] = useState("2");
  
  const [qualSurveyQualifierEnabled, setQualSurveyQualifierEnabled] = useState(false);
  const [qualSurveyQualifier, setQualSurveyQualifier] = useState("In this culture…");
  const [qualSurveyQualifierFr, setQualSurveyQualifierFr] = useState("");
  
  const [qualCustomSurveyHeaderEnabled, setQualCustomSurveyHeaderEnabled] = useState(false);
  const [qualCustomSurveyHeader, setQualCustomSurveyHeader] = useState("Culture Question");
  const [qualCustomSurveyHeaderFr, setQualCustomSurveyHeaderFr] = useState("");
  const [qualCustomSurveyHeaderColor, setQualCustomSurveyHeaderColor] = useState("");
  
  const [qualRemoveSurveyQualifierEnabled, setQualRemoveSurveyQualifierEnabled] = useState(false);
  
  const [qualUseImageAsQualifierEnabled, setQualUseImageAsQualifierEnabled] = useState(false);
  const [qualImageUrl, setQualImageUrl] = useState("");
  const [qualImageHeight, setQualImageHeight] = useState("");

  const LANGUAGE_OPTIONS = [
    { key: "FR", value: "FR", text: "French (FR)" },
    { key: "POR", value: "POR", text: "Portuguese (POR)" },
    { key: "ES", value: "ES", text: "Spanish (ES)" },
  ];

  const isAdvancedOpen = accordionValue.includes("advanced");
  const isQualifierOpen = accordionValue.includes("qualifier");
  const toggleAdvancedSection = () => {
    setAccordionValue((current) =>
      current.includes("advanced")
        ? current.filter((item) => item !== "advanced")
        : [...current, "advanced"]
    );
  };
  const toggleQualifierSection = () => {
    setAccordionValue((current) =>
      current.includes("qualifier")
        ? current.filter((item) => item !== "qualifier")
        : [...current, "qualifier"]
    );
  };

  const LIKERT_OPTIONS = [
    { key: 5, value: 5, text: "5" },
    { key: 6, value: 6, text: "6" },
    { key: 7, value: 7, text: "7" },
  ];

  useEffect(() => {
    setQuestionId(id);
  }, [id]);

  useEffect(() => {
    if (questionId) {
      dispatch({
        type: SG_FETCH_SURVEY_QUESTION,
        payload: { id: questionId },
      });
    }
  }, [dispatch, questionId]);

  const { get_questions } = useSelector(
    (state) => ({
      get_questions: state.surveyquestion,
    }),
    shallowEqual
  );

  useEffect(() => {
    setSurveyQuestion(
      arrange_Questions(get_questions?.[questionId]?.questions)
    );
    setAskComments(
      get_questions?.[questionId]?.questions?.comments || []
    );
  }, [get_questions, questionId]);

  useEffect(() => {
    const details = get_questions?.[questionId];
    setQuestionDetails(details);
  }, [get_questions, questionId]);

  useEffect(() => {
    const base = questionDetails?.questions;
    if (!base) {
      setJsonDraft("{}");
      setParsedJson(null);
      setIsValid(false);
      setValidationSummary("");
      return;
    }
    try {
      const serialized = JSON.stringify(base, null, 2);
      setJsonDraft(serialized);
      setParsedJson(base);
      setValidationSummary("JSON is valid!");
      setIsValid(true);
    } catch (error) {
      setJsonDraft("{}");
      setParsedJson(null);
      setValidationSummary("Unable to prepare JSON view");
      setIsValid(false);
    }
  }, [questionDetails?.questions]);

const handlePaste = (event) => {
    const clipboardData =
      event.clipboardData || window.clipboardData;
    const pastedData = clipboardData.getData("Text");
    validateJson(pastedData);
  };

  const validateJson = (data) => {
    setJsonDraft(data);
    try {
      const parsed = JSON.parse(data);
      setParsedJson(parsed);
      setValidationSummary("JSON is valid!");
      setIsValid(true);
    } catch (error) {
      setParsedJson(null);
      setIsValid(false);
      setValidationSummary(`JSON is not valid: ${error.message}`);
    }
  };

  const UpdateComments = (commentsToSave) => {
    // Use the passed comments if provided, otherwise use the current state
    const commentsData = commentsToSave !== undefined ? commentsToSave : askComments;
    
    const base = parsedJson
      ? JSON.parse(JSON.stringify(parsedJson))
      : JSON.parse(JSON.stringify(questionDetails?.questions || {}));
    if (base && typeof base === "object" && !Array.isArray(base)) {
      base.comments = commentsData;
    }
        dispatch({
          type: SG_EDIT_SURVEY_QUESTION,
          payload: {
            id: questionId,
        questions: base,
          },
        });
    setParsedJson(base);
    setJsonDraft(JSON.stringify(base, null, 2));
  };

  const onSave = () => {
    if (!questionId || !isValid || !parsedJson) {
      setOpenCreateNew(false);
      return;
    }
    const next = JSON.parse(JSON.stringify(parsedJson));
    if (next && typeof next === "object" && !Array.isArray(next)) {
      next.comments = askComments;
    }
    dispatch({
      type: SG_EDIT_SURVEY_QUESTION,
      payload: {
        id: questionId,
        questions: next,
      },
    });
    setOpenCreateNew(false);
  };

  const handleSaveIntroScreens = ({ screens, defaults }) => {
    const next = { ...(questionDetails?.questions || {}) };
    next.custom_intro_screens = screens || [];
    next.default_intro_screens = {
      default_intro: !!defaults?.default_intro,
      default_privacy: !!defaults?.default_privacy,
      default_private_response: !!defaults?.default_private_response,
      languages: Array.isArray(defaults?.languages) ? defaults.languages : [],
    };
    dispatch({
      type: SG_EDIT_SURVEY_QUESTION,
      payload: { id: questionId, questions: next },
    });
    setOpenIntroScreens(false);
  };

  const handleSaveEndScreens = ({ screens, languages }) => {
    const next = { ...(questionDetails?.questions || {}) };
    next.custom_end_screens = Array.isArray(screens) ? screens : [];
    if (Array.isArray(languages)) {
      next.language = languages;
    }
    dispatch({
      type: SG_EDIT_SURVEY_QUESTION,
      payload: { id: questionId, questions: next },
    });
    setOpenEndScreens(false);
  };

  const handleSaveAnalytics = (updatedQuestions) => {
    dispatch({
      type: SG_EDIT_SURVEY_QUESTION,
      payload: {
        id: questionId,
        questions: { ...(updatedQuestions || {}) },
      },
    });
    setOpenAnalyticsManager(false);
  };

  const BackArrow = () => {
    navigate("/app/questions");
  };

  const existingIntroScreens =
    questionDetails?.questions?.custom_intro_screens || [];
  const existingDefaultIntro =
    questionDetails?.questions?.default_intro_screens || {};
  const existingEndScreens =
    questionDetails?.questions?.custom_end_screens || [];
  const existingEndLanguages =
    (questionDetails?.questions && questionDetails.questions.language) || [];
  const hasAnalytics = !!questionDetails?.questions?.analytics;
  const isRestrictedSurveyType =
    questionDetails?.survey_type === 10 || questionDetails?.survey_type === 11;

  useEffect(() => {
    if (!isAdvancedOpen) return;
      const q = questionDetails?.questions || {};
      setAdvAllowSkip(!!q.allowSkip);
      setAdvAllowSkipComments(!!q.allowSkipComments);
    setAdvLanguages(
      Array.isArray(q.language) ? q.language.filter((lc) => lc !== "EN") : []
    );
    setAdvUseLikert(
      typeof q.useLikert === "number" ? q.useLikert : null
    );
      setAdvQuestionTitles(!!q.Question_titles);

      const baseCat = (() => {
        if (typeof q.category_titles === "string") {
          return { EN: q.category_titles };
        }
      if (
        q &&
        typeof q.category_titles === "object" &&
        q.category_titles !== null
      ) {
          return { ...q.category_titles };
        }
        return { EN: "" };
      })();

      const normalized = { EN: baseCat.EN || baseCat.en || "" };
    Object.keys(baseCat || {}).forEach((key) => {
      const upper = String(key).toUpperCase();
        if (upper === "EN") return;
      normalized[upper] = baseCat[key] || "";
      });

      const withLangs = { ...normalized };
    (Array.isArray(q.language)
      ? q.language.filter((lc) => String(lc).toUpperCase() !== "EN")
      : []
    ).forEach((lc) => {
        const norm = String(lc).toUpperCase();
        if (withLangs[norm] === undefined) withLangs[norm] = "";
      });
      setAdvCategoryTitles(withLangs);

      const langs = Array.isArray(q.language) ? q.language : [];

    const thanksInit = { text: q?.custom_thanks?.text || "" };
      langs.forEach((lc) => {
        const lower = String(lc).toLowerCase();
        if (lower === "en") return;
      thanksInit[lower] =
        q?.custom_thanks?.[lower] || q?.custom_thanks?.[lc] || "";
      });
      setAdvCustomThanks(thanksInit);

    const introInit = { text: q?.custom_category_intro?.text || "" };
      langs.forEach((lc) => {
        const lower = String(lc).toLowerCase();
        if (lower === "en") return;
      introInit[lower] =
        q?.custom_category_intro?.[lower] ||
        q?.custom_category_intro?.[lc] ||
        "";
    });
    setAdvCustomCategoryIntro(introInit);

    const introTitleBase = (() => {
      const t = q?.custom_category_intro?.title;
        if (!t) return { EN: "" };
        if (typeof t === "string") return { EN: t };
      if (typeof t === "object") return { ...t };
        return { EN: "" };
      })();
    const introTitleNorm = { EN: introTitleBase.EN || introTitleBase.en || "" };
    Object.keys(introTitleBase || {}).forEach((key) => {
      const upper = String(key).toUpperCase();
        if (upper === "EN") return;
      introTitleNorm[upper] = introTitleBase[key] || "";
    });
    (Array.isArray(q.language)
      ? q.language.filter((lc) => String(lc).toUpperCase() !== "EN")
      : []
    ).forEach((lc) => {
        const norm = String(lc).toUpperCase();
      if (introTitleNorm[norm] === undefined) introTitleNorm[norm] = "";
      });
    setAdvCustomCategoryIntroTitle(introTitleNorm);

      const highVal = Number(q?.high_follow_up_score);
      const lowVal = Number(q?.low_follow_up_score);
    const clampScore = (n) => Math.max(0, Math.min(10, n));

      if (Number.isFinite(highVal)) {
        setAdvHighFollowUpEnabled(highVal >= 0 && highVal <= 10);
      setAdvHighFollowUpScore(clampScore(highVal));
      } else {
        setAdvHighFollowUpEnabled(false);
        setAdvHighFollowUpScore(10);
      }

      if (Number.isFinite(lowVal)) {
        setAdvLowFollowUpEnabled(lowVal >= 0 && lowVal <= 10);
      setAdvLowFollowUpScore(clampScore(lowVal));
      } else {
        setAdvLowFollowUpEnabled(false);
        setAdvLowFollowUpScore(0);
      }

      setAdvDemographicsAtEnd(!!q?.demographics_at_end);
      setAdvRemoveCategoryThanks(!!q?.remove_category_thanks);
      setAdvDisableEngagementAI(!!q?.disable_engagement_ai);
  }, [isAdvancedOpen, questionDetails]);

  useEffect(() => {
    if (!isQualifierOpen) return;
    const customHeaders = questionDetails?.questions?.custom_headers || {};
    
    // Organization
    const hasOrg = customHeaders.organization !== undefined;
    setQualOrganizationEnabled(hasOrg);
    setQualOrganization(hasOrg ? String(customHeaders.organization) : "2");
    
    // Survey Qualifier
    const hasSurveyQual = customHeaders.survey_qualifer !== undefined;
    setQualSurveyQualifierEnabled(hasSurveyQual);
    setQualSurveyQualifier(customHeaders.survey_qualifer || "In this culture…");
    setQualSurveyQualifierFr(customHeaders.survey_qualifier_fr || "");
    
    // Custom Survey Header
    const hasCustomHeader = customHeaders.custom_survey_header !== undefined;
    setQualCustomSurveyHeaderEnabled(hasCustomHeader);
    setQualCustomSurveyHeader(customHeaders.custom_survey_header || "Culture Question");
    setQualCustomSurveyHeaderFr(customHeaders.custom_survey_header_fr || "");
    setQualCustomSurveyHeaderColor(customHeaders.custom_survey_header_color || "");
    
    // Remove Survey Qualifier
    const hasRemoveQual = customHeaders.remove_survey_qualifer !== undefined;
    setQualRemoveSurveyQualifierEnabled(hasRemoveQual);
    
    // Use Image as Qualifier
    const hasImageQual = customHeaders.use_image_as_qualifier !== undefined;
    setQualUseImageAsQualifierEnabled(hasImageQual);
    setQualImageUrl(customHeaders.image_url || "");
    setQualImageHeight(customHeaders.image_height || "");
  }, [isQualifierOpen, questionDetails]);

  const onSaveAdvanced = () => {
    console.log('🔵 onSaveAdvanced CALLED');
    console.log('🔵 advUseLikert value:', advUseLikert);

    const baseQuestions = { ...(questionDetails?.questions || {}) };
    const updatedQuestions = { ...baseQuestions };

    updatedQuestions.allowSkip = !!advAllowSkip;
    updatedQuestions.allowSkipComments = !!advAllowSkipComments;
    updatedQuestions.Question_titles = !!advQuestionTitles;
    if (advLanguages && advLanguages.length > 0) {
      updatedQuestions.language = advLanguages;
    } else {
      delete updatedQuestions.language;
    }

    const catIn = { ...(advCategoryTitles || {}) };
    const out = { EN: catIn.EN || catIn.en || "" };
    const langs = Array.isArray(updatedQuestions.language)
      ? updatedQuestions.language.map((l) => String(l).toUpperCase())
      : [];
    langs.forEach((lc) => {
      const val = catIn[lc] ?? catIn[lc.toLowerCase()] ?? "";
      out[lc] = typeof val === "string" ? val : "";
    });
    updatedQuestions.category_titles = out;

    const thanksIn = { ...(advCustomThanks || {}) };
    const thanksOut = { text: thanksIn.text || "" };
    (Array.isArray(updatedQuestions.language) ? updatedQuestions.language : []).forEach((lc) => {
      const lower = String(lc).toLowerCase();
      if (lower === "en") return;
      const val = thanksIn[lower];
      thanksOut[lower] = typeof val === "string" ? val : "";
    });
    updatedQuestions.custom_thanks = thanksOut;

    if (advDemographicsAtEnd) {
      const introIn = { ...(advCustomCategoryIntro || {}) };
      const introOut = { text: introIn.text || "" };
      (Array.isArray(updatedQuestions.language) ? updatedQuestions.language : []).forEach((lc) => {
        const lower = String(lc).toLowerCase();
        if (lower === "en") return;
        const val = introIn[lower];
        introOut[lower] = typeof val === "string" ? val : "";
      });

      const titleIn = { ...(advCustomCategoryIntroTitle || {}) };
      const titleOut = { EN: titleIn.EN || titleIn.en || "" };
      (Array.isArray(updatedQuestions.language)
        ? updatedQuestions.language.map((l) => String(l).toUpperCase())
        : []
      ).forEach((lc) => {
        if (lc === "EN") return;
        const val = titleIn[lc] ?? titleIn[lc.toLowerCase()] ?? "";
        titleOut[lc] = typeof val === "string" ? val : "";
      });
      updatedQuestions.custom_category_intro = { ...introOut, title: titleOut };
    } else if (updatedQuestions.custom_category_intro) {
      delete updatedQuestions.custom_category_intro;
    }

    const clampScore = (n) => Math.max(0, Math.min(10, Math.round(n)));
    if (advHighFollowUpEnabled) {
      updatedQuestions.high_follow_up_score = clampScore(advHighFollowUpScore);
    } else if (updatedQuestions.high_follow_up_score !== undefined) {
      delete updatedQuestions.high_follow_up_score;
    }

    if (advLowFollowUpEnabled) {
      updatedQuestions.low_follow_up_score = clampScore(advLowFollowUpScore);
    } else if (updatedQuestions.low_follow_up_score !== undefined) {
      delete updatedQuestions.low_follow_up_score;
    }

    updatedQuestions.demographics_at_end = !!advDemographicsAtEnd;
    updatedQuestions.remove_category_thanks = !!advRemoveCategoryThanks;
    updatedQuestions.disable_engagement_ai = !!advDisableEngagementAI;

    if (advUseLikert === 5 || advUseLikert === 6 || advUseLikert === 7) {
      updatedQuestions.useLikert = advUseLikert;
    } else {
      delete updatedQuestions.useLikert;
    }

    const normalizedQuestions = JSON.parse(JSON.stringify(updatedQuestions));
    const updatedJsonDraft = JSON.stringify(normalizedQuestions, null, 2);

    console.log('=== SAVING ADVANCED SETTINGS ===');
    console.log('useLikert in current:', 'useLikert' in normalizedQuestions);
    console.log('current.useLikert value:', normalizedQuestions.useLikert);
    console.log('Full current object:', updatedJsonDraft);

    dispatch({
      type: SG_EDIT_SURVEY_QUESTION,
      payload: { id: questionId, questions: normalizedQuestions },
    });

    setQuestionDetails((prev) =>
      prev ? { ...prev, questions: normalizedQuestions } : prev
    );
    setParsedJson(normalizedQuestions);
    setJsonDraft(updatedJsonDraft);

    setAccordionValue((current) => current.filter((item) => item !== "advanced"));
  };

  const onSaveQualifier = () => {
    const current = { ...(questionDetails?.questions || {}) };
    
    const customHeaders = {};
    
    // Only add enabled fields to custom_headers
    if (qualOrganizationEnabled) {
      customHeaders.organization = qualOrganization;
    }
    
    if (qualSurveyQualifierEnabled) {
      customHeaders.survey_qualifer = qualSurveyQualifier;
      if (qualSurveyQualifierFr) {
        customHeaders.survey_qualifier_fr = qualSurveyQualifierFr;
      }
    }
    
    if (qualCustomSurveyHeaderEnabled) {
      customHeaders.custom_survey_header = qualCustomSurveyHeader;
      if (qualCustomSurveyHeaderFr) {
        customHeaders.custom_survey_header_fr = qualCustomSurveyHeaderFr;
      }
      if (qualCustomSurveyHeaderColor) {
        customHeaders.custom_survey_header_color = qualCustomSurveyHeaderColor;
      }
    }
    
    if (qualRemoveSurveyQualifierEnabled) {
      customHeaders.remove_survey_qualifer = "true";
    }
    
    if (qualUseImageAsQualifierEnabled) {
      customHeaders.use_image_as_qualifier = "true";
      if (qualImageUrl) {
        customHeaders.image_url = qualImageUrl;
      }
      if (qualImageHeight) {
        customHeaders.image_height = qualImageHeight;
      }
    }
    
    // Only set custom_headers if there are any enabled fields
    if (Object.keys(customHeaders).length > 0) {
      current.custom_headers = customHeaders;
    } else {
      // Remove custom_headers if nothing is enabled
      delete current.custom_headers;
    }
    
    dispatch({
      type: SG_EDIT_SURVEY_QUESTION,
      payload: { id: questionId, questions: current },
    });
    setAccordionValue((current) => current.filter((item) => item !== "qualifier"));
  };

  const jsonPreview = useMemo(() => {
    if (parsedJson) return parsedJson;
    return questionDetails?.questions || {};
  }, [parsedJson, questionDetails?.questions]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 px-2"
          onClick={BackArrow}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back
        </Button>
        <div className="flex flex-1 flex-col">
          <h1 className="text-2xl font-semibold text-foreground">
            {questionDetails?.name || "Question details"}
          </h1>
          <p className="text-sm text-muted-foreground">
            Last updated {formatTimestamp(questionDetails?.updated_at)} · Created {formatTimestamp(questionDetails?.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowOverview(true)}>
            View overview
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <Card className="border-border/60">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">Quick actions</CardTitle>
              <CardDescription>Common tasks for this question</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2">
                <Button variant="outline" className="justify-start" onClick={() => navigate(`/app/questions/edit/${questionId}`)}>
                  <Pencil className="mr-2 h-4 w-4" /> Edit question
                </Button>
                <Button variant="outline" className="justify-start" onClick={() => setOpenSummary(true)}>
                  <PanelsTopLeft className="mr-2 h-4 w-4" /> View structure
                </Button>
                <Button variant="outline" className="justify-start" onClick={() => setOpenComment(true)}>
                  <MessageCircle className="mr-2 h-4 w-4" /> Manage comments
                </Button>
                {!isRestrictedSurveyType && (
                  <>
                    <Button variant="outline" className="justify-start" onClick={() => setOpenStructureManager(true)}>
                      <Workflow className="mr-2 h-4 w-4" /> Dimensions & factors
                    </Button>
                    <Button variant="outline" className="justify-start" onClick={() => setOpenOrderManager(true)}>
                      <Shuffle className="mr-2 h-4 w-4" /> Reorder questions
                    </Button>
                    <Button variant="outline" className="justify-start" onClick={() => setOpenAnalyticsManager(true)}>
                      <BarChart3 className="mr-2 h-4 w-4" /> Analytics {hasAnalytics ? "(configured)" : "(pending)"}
                    </Button>
                  </>
                )}
                <div className="my-1" />
                <Button variant="outline" className="justify-start" onClick={() => setOpenIntroScreens(true)}>
                  <Eye className="mr-2 h-4 w-4" /> Intro screens ({existingIntroScreens?.length || 0})
                </Button>
                {questionDetails?.survey_type === 6 && (
                  <Button variant="outline" className="justify-start" onClick={() => setOpenEndScreens(true)}>
                    <Eye className="mr-2 h-4 w-4" /> End screens ({existingEndScreens?.length || 0})
                  </Button>
                )}
                {questionDetails?.survey_type === 10 && (
                  <Button variant="outline" className="justify-start" onClick={() => setOpenOutcomeStructureManager(true)}>
                    <Workflow className="mr-2 h-4 w-4" /> Outcome structure
                  </Button>
                )}
                {questionDetails?.survey_type === 11 && (
                  <Button variant="outline" className="justify-start" onClick={() => setOpenOutcomeFeedbackStructureManager(true)}>
                    <ClipboardList className="mr-2 h-4 w-4" /> Outcome feedback
                  </Button>
                )}
                <div className="my-1" />
                <Button variant="outline" className="justify-start" onClick={() => setOpenCreateNew(true)}>
                  <FileCode className="mr-2 h-4 w-4" /> Replace JSON
                </Button>
                {!isRestrictedSurveyType && (
                  <>
                    <Button variant="outline" className="justify-start" onClick={toggleAdvancedSection}>
                      <SlidersHorizontal className="mr-2 h-4 w-4" /> {isAdvancedOpen ? "Hide advanced" : "Advanced settings"}
                    </Button>
                    <Button variant="outline" className="justify-start" onClick={toggleQualifierSection}>
                      <SlidersHorizontal className="mr-2 h-4 w-4" /> {isQualifierOpen ? "Hide qualifier" : "Qualifier options"}
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
        {!isRestrictedSurveyType && (
          <Accordion
            type="multiple"
            value={accordionValue}
            onValueChange={setAccordionValue}
            className="space-y-3"
          >
          <AccordionItem value="advanced">
            <AccordionTrigger>Advanced settings</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-6 rounded-lg border border-border/50 bg-card/40 p-4">
                <div className="grid gap-6 md:grid-cols-2">
                  <Tile title="Skip logic" description="Allow respondents to skip questions">
                    <CheckboxRow
                      label="Allow skip"
                      checked={advAllowSkip}
                      onCheckedChange={setAdvAllowSkip}
                    />
                    <CheckboxRow
                      label="Allow skip comments"
                      description="Allow respondents to skip comment questions"
                      checked={advAllowSkipComments}
                      onCheckedChange={setAdvAllowSkipComments}
                    />
                  </Tile>

                  <Tile title="Likert scale" description="Control the scale length">
                    <Select
                      value={advUseLikert === null ? undefined : String(advUseLikert)}
                      onValueChange={(value) => {
                        if (value === "__clear") {
                          setAdvUseLikert(null);
                          return;
                        }
                        setAdvUseLikert(Number(value));
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select Likert scale" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__clear" className="text-muted-foreground">
                          Clear selection
                        </SelectItem>
                        <SelectSeparator />
                        {LIKERT_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={String(option.value)}>
                            {option.text}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Tile>

                  <Tile
                    className="md:col-span-2"
                    title="Languages"
                    description="Add additional language support"
                  >
                    <MultiSelect
                      options={LANGUAGE_OPTIONS}
                      value={advLanguages}
                      onChange={(next) => setAdvLanguages(next)}
                      placeholder="Select languages"
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave empty to keep English only.
                    </p>
                  </Tile>
                </div>

                <div className="rounded-lg border border-border/50 bg-card/40">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold">Presentation</CardTitle>
                    <CardDescription>Control titles and localized copy</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div>
                      <LabelField label="Category title (EN)">
                        <Input
                          value={advCategoryTitles?.EN || ""}
                          onChange={(event) =>
                            setAdvCategoryTitles((prev) => ({
                              ...(prev || {}),
                              EN: event.target.value,
                            }))
                          }
                          placeholder="Category title"
                        />
                      </LabelField>
                      <div className="mt-3 space-y-3">
                        {(advLanguages || []).map((lc) => {
                          const norm = String(lc).toUpperCase();
                          return (
                            <LabelField
                              key={norm}
                              label={`Category title (${norm})`}
                            >
                              <Input
                                value={advCategoryTitles?.[norm] || ""}
                                onChange={(event) =>
                                  setAdvCategoryTitles((prev) => ({
                                    ...(prev || {}),
                                    [norm]: event.target.value,
                                  }))
                                }
                              />
                            </LabelField>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <LabelField label="Custom thanks (EN)">
                        <Textarea
                          rows={3}
                          value={advCustomThanks?.text || ""}
                          onChange={(event) =>
                            setAdvCustomThanks((prev) => ({
                              ...(prev || {}),
                              text: event.target.value,
                            }))
                          }
                          placeholder="Thank participants for completing the survey"
                        />
                      </LabelField>
                      <div className="mt-3 space-y-3">
                        {(advLanguages || []).map((lc) => {
                          const lower = String(lc).toLowerCase();
                          if (lower === "en") return null;
                          return (
                            <LabelField
                              key={lower}
                              label={`Custom thanks (${lower.toUpperCase()})`}
                            >
                              <Textarea
                                rows={3}
                                value={advCustomThanks?.[lower] || ""}
                                onChange={(event) =>
                                  setAdvCustomThanks((prev) => ({
                                    ...(prev || {}),
                                    [lower]: event.target.value,
                                  }))
                                }
                              />
                            </LabelField>
                          );
                        })}
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <Tile
                        title="Remove category thanks"
                        description="Hide category-specific thanks copy"
                      >
                        <Checkbox
                          checked={advRemoveCategoryThanks}
                          onCheckedChange={(val) =>
                            setAdvRemoveCategoryThanks(!!val)
                          }
                        />
                      </Tile>
                      <Tile
                        title="Show question titles"
                        description="Display internal question titles"
                      >
                        <Checkbox
                          checked={advQuestionTitles}
                          onCheckedChange={(val) => setAdvQuestionTitles(!!val)}
                        />
                      </Tile>
                    </div>

                    <Tile
                      title="Disable Engagement AI"
                      description="This disables the AI generated follow ups in the high and low conditional feedback"
                    >
                      <CheckboxRow
                        label="Disable AI generated follow ups"
                        description="Prevents AI-generated follow-up questions in high and low conditional feedback"
                        checked={advDisableEngagementAI}
                        onCheckedChange={setAdvDisableEngagementAI}
                      />
                    </Tile>

                    <Tile
                      title="Demographics placement"
                      description="Move demographic questions to the end of the survey"
                    >
                      <CheckboxRow
                        label="Show demographics at end"
                        checked={advDemographicsAtEnd}
                        onCheckedChange={setAdvDemographicsAtEnd}
                      />

                      {advDemographicsAtEnd && (
                        <div className="mt-4 space-y-3">
                          <LabelField label="Custom category intro title (EN)">
                            <Input
                              value={advCustomCategoryIntroTitle?.EN || ""}
                              onChange={(event) =>
                                setAdvCustomCategoryIntroTitle((prev) => ({
                                  ...(prev || {}),
                                  EN: event.target.value,
                                }))
                              }
                              placeholder="Intro title"
                            />
                          </LabelField>
                          {(advLanguages || []).map((lc) => {
                            const norm = String(lc).toUpperCase();
                            return (
                              <LabelField
                                key={norm}
                                label={`Custom category intro title (${norm})`}
                              >
                                <Input
                                  value={advCustomCategoryIntroTitle?.[norm] || ""}
                                  onChange={(event) =>
                                    setAdvCustomCategoryIntroTitle((prev) => ({
                                      ...(prev || {}),
                                      [norm]: event.target.value,
                                    }))
                                  }
                                />
                              </LabelField>
                            );
                          })}

                          <LabelField label="Custom category intro (EN)">
                            <Textarea
                              rows={3}
                              value={advCustomCategoryIntro?.text || ""}
                              onChange={(event) =>
                                setAdvCustomCategoryIntro((prev) => ({
                                  ...(prev || {}),
                                  text: event.target.value,
                                }))
                              }
                              placeholder="Custom introduction copy"
                            />
                          </LabelField>
                          {(advLanguages || []).map((lc) => {
                            const lower = String(lc).toLowerCase();
                            if (lower === "en") return null;
                            return (
                              <LabelField
                                key={lower}
                                label={`Custom category intro (${lower.toUpperCase()})`}
                              >
                                <Textarea
                                  rows={3}
                                  value={advCustomCategoryIntro?.[lower] || ""}
                                  onChange={(event) =>
                                    setAdvCustomCategoryIntro((prev) => ({
                                      ...(prev || {}),
                                      [lower]: event.target.value,
                                    }))
                                  }
                                />
                              </LabelField>
                            );
                          })}
                        </div>
                      )}
                    </Tile>
                  </CardContent>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-3">
                  <Button variant="ghost" onClick={() => setAccordionValue((current) => current.filter((item) => item !== "advanced"))}>
                    Cancel
                  </Button>
                  <Button onClick={onSaveAdvanced}>Save changes</Button>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="qualifier">
            <AccordionTrigger>Qualifier Options</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-6 rounded-lg border border-border/50 bg-card/40 p-4">
                <div className="rounded-lg border border-border/50 bg-card/40">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold">Survey Qualifiers & Headers</CardTitle>
                    <CardDescription>Customize how survey questions are presented. Toggle to enable/disable each option.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    
                    {/* Organization Setting */}
                    <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={qualOrganizationEnabled}
                          onCheckedChange={(val) => setQualOrganizationEnabled(!!val)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm text-foreground">Organization Setting</div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Removes duplicate primary after the category questions
                          </p>
                        </div>
                      </div>
                      {qualOrganizationEnabled && (
                        <div className="ml-7 pt-2">
                          <LabelField label="Value">
                            <Input
                              type="number"
                              value={qualOrganization}
                              onChange={(e) => setQualOrganization(e.target.value)}
                              placeholder="2"
                              className="max-w-[120px]"
                            />
                          </LabelField>
                        </div>
                      )}
                    </div>

                    {/* Survey Qualifier */}
                    <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={qualSurveyQualifierEnabled}
                          onCheckedChange={(val) => setQualSurveyQualifierEnabled(!!val)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm text-foreground">Survey Qualifier</div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Customizes the qualifier before the survey question is asked
                          </p>
                        </div>
                      </div>
                      {qualSurveyQualifierEnabled && (
                        <div className="ml-7 pt-2 space-y-4">
                          <LabelField label="English (EN)">
                            <Input
                              value={qualSurveyQualifier}
                              onChange={(e) => setQualSurveyQualifier(e.target.value)}
                              placeholder="In this culture…"
                            />
                          </LabelField>
                          <div className="pl-4 border-l-2 border-muted-foreground/20">
                            <LabelField label="French (FR) - Optional">
                              <Input
                                value={qualSurveyQualifierFr}
                                onChange={(e) => setQualSurveyQualifierFr(e.target.value)}
                                placeholder="Dans cette culture…"
                              />
                            </LabelField>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Custom Survey Header */}
                    <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={qualCustomSurveyHeaderEnabled}
                          onCheckedChange={(val) => setQualCustomSurveyHeaderEnabled(!!val)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm text-foreground">Custom Survey Header</div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Customizes the head of the survey
                          </p>
                        </div>
                      </div>
                      {qualCustomSurveyHeaderEnabled && (
                        <div className="ml-7 pt-2 space-y-4">
                          <LabelField label="English (EN)">
                            <Input
                              value={qualCustomSurveyHeader}
                              onChange={(e) => setQualCustomSurveyHeader(e.target.value)}
                              placeholder="Culture Question"
                            />
                          </LabelField>
                          <div className="pl-4 border-l-2 border-muted-foreground/20">
                            <LabelField label="French (FR) - Optional">
                              <Input
                                value={qualCustomSurveyHeaderFr}
                                onChange={(e) => setQualCustomSurveyHeaderFr(e.target.value)}
                                placeholder="Question culturelle"
                              />
                            </LabelField>
                          </div>
                          <div className="pl-4 border-l-2 border-muted-foreground/20">
                            <LabelField label="Header color - Optional">
                              <div className="flex items-center gap-3">
                                <SurveyHeaderColorPicker
                                  color={qualCustomSurveyHeaderColor || "#6a00ff"}
                                  setColor={(c) => setQualCustomSurveyHeaderColor(c.hex)}
                                />
                                <Input
                                  className="max-w-[160px]"
                                  value={qualCustomSurveyHeaderColor}
                                  onChange={(e) => setQualCustomSurveyHeaderColor(e.target.value)}
                                  placeholder="#6a00ff"
                                />
                              </div>
                            </LabelField>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Use Image as Qualifier */}
                    <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={qualUseImageAsQualifierEnabled}
                          onCheckedChange={(val) => setQualUseImageAsQualifierEnabled(!!val)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm text-foreground">Use Image as Qualifier</div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Display an image as the qualifier before survey questions
                          </p>
                        </div>
                      </div>
                      {qualUseImageAsQualifierEnabled && (
                        <div className="ml-7 pt-2 space-y-4">
                          <LabelField label="Image URL">
                            <Input
                              value={qualImageUrl}
                              onChange={(e) => setQualImageUrl(e.target.value)}
                              placeholder="https://example.com/image.jpg"
                            />
                          </LabelField>
                          <LabelField label="Image Height">
                            <Input
                              type="number"
                              value={qualImageHeight}
                              onChange={(e) => setQualImageHeight(e.target.value)}
                              placeholder="e.g. 200"
                              className="max-w-[120px]"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Height in pixels (optional)
                            </p>
                          </LabelField>
                        </div>
                      )}
                    </div>

                    {/* Remove Survey Qualifier */}
                    <div className="rounded-lg border border-border/60 bg-muted/20 p-4">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={qualRemoveSurveyQualifierEnabled}
                          onCheckedChange={(val) => setQualRemoveSurveyQualifierEnabled(!!val)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm text-foreground">Remove Survey Qualifier</div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            When enabled, removes the qualifier from survey questions entirely
                          </p>
                        </div>
                      </div>
                    </div>

                  </CardContent>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-3">
                  <Button variant="ghost" onClick={() => setAccordionValue((current) => current.filter((item) => item !== "qualifier"))}>
                    Cancel
                  </Button>
                  <Button onClick={onSaveQualifier}>Save changes</Button>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="json">
            <AccordionTrigger>Question JSON</AccordionTrigger>
            <AccordionContent>
              <div className="rounded-lg border border-border/60 bg-card/40 p-4">
                <ReactJson
                  src={jsonPreview}
                  collapsed={2}
                  enableClipboard={false}
                  displayDataTypes={false}
                  name={null}
                />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        )}
        </div>
      </div>

      <Dialog open={showOverview} onOpenChange={setShowOverview}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Question overview</DialogTitle>
            <DialogDescription>
              Snapshot of metadata and setup state for this question.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6">
            <div className="grid gap-3 sm:grid-cols-2">
              <InfoItem label="Question name" value={questionDetails?.name} />
              <InfoItem label="Sort order" value={questionDetails?.sort_order} />
              <InfoItem label="Survey type" value={resolveSurveyTypeLabel(questionDetails?.survey_type)} />
              <InfoItem label="Status" value={resolveStatusLabel(questionDetails?.status)} badgeVariant="info" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <InfoItem label="Created" value={formatTimestamp(questionDetails?.created_at)} />
              <InfoItem label="Last updated" value={formatTimestamp(questionDetails?.updated_at)} />
              <InfoItem label="URL location" value={questionDetails?.url_location} isMonospace />
              <InfoItem label="Analytics" value={hasAnalytics ? "Configured" : "Pending"} badgeVariant={hasAnalytics ? "success" : "warning"} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <InfoItem label="Intro screens" value={`${existingIntroScreens?.length || 0} configured`} />
              {questionDetails?.survey_type === 6 ? (
                <InfoItem label="End screens" value={`${existingEndScreens?.length || 0} configured`} />
              ) : (
                <InfoItem label="End screens" value="Not applicable" />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOverview(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openSummary} onOpenChange={setOpenSummary}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Question list ({surveyQuestion?.length || 0})
            </DialogTitle>
            <DialogDescription>
              Flattened question data to help with quick review.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto rounded-md border bg-muted/40 p-4 text-xs">
            <pre className="whitespace-pre-wrap">
              {JSON.stringify(surveyQuestion, null, 2)}
            </pre>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpenSummary(false)}>
              Close
          </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openComment} onOpenChange={setOpenComment}>
        <DialogContent className="max-h-[85vh] max-w-4xl space-y-4 overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Comment questions ({askComments?.length || 0})
            </DialogTitle>
            <DialogDescription>
              Manage comment follow-ups and their ordering.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[40vh] overflow-auto rounded-md border bg-muted/40 p-4 text-xs">
            <pre className="whitespace-pre-wrap">
              {JSON.stringify(askComments, null, 2)}
            </pre>
          </div>
          <AddEditComment
            totalQuestions={surveyQuestion?.length + askComments?.length}
            askComments={askComments}
            setAskComments={setAskComments}
            updated={updated}
            setUpdated={setUpdated}
            UpdateComments={UpdateComments}
          />
          <DialogFooter>
            <Button onClick={() => setOpenComment(false)}>Save & close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openCreateNew} onOpenChange={setOpenCreateNew}>
        <DialogContent className="max-h-[85vh] max-w-4xl space-y-4 overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Replace entire question JSON</DialogTitle>
            <DialogDescription>
              Paste valid JSON to overwrite the question configuration.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Paste your JSON here..."
            value={jsonDraft}
            onPaste={handlePaste}
            onChange={(event) => validateJson(event.target.value)}
            className="min-h-[320px] font-mono text-xs"
          />
          {validationSummary && (
            <div
              className={cn(
                "rounded-md border px-3 py-2 text-sm",
                isValid
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-destructive/40 bg-destructive/10 text-destructive"
              )}
            >
              {validationSummary}
            </div>
          )}
          <AddEditComment
            totalQuestions={surveyQuestion?.length + askComments?.length}
            askComments={askComments}
            setAskComments={setAskComments}
            updated={updated}
            setUpdated={setUpdated}
            UpdateComments={UpdateComments}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreateNew(false)}>
              Cancel
          </Button>
            <Button onClick={onSave} disabled={!isValid}>
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!isRestrictedSurveyType && (
        <CustomIntroScreensModal
          open={openIntroScreens}
          onClose={() => setOpenIntroScreens(false)}
          existingScreens={existingIntroScreens}
          existingDefaults={existingDefaultIntro}
          onSave={handleSaveIntroScreens}
        />
      )}
      {questionDetails?.survey_type === 6 && (
        <CustomEndScreensModal
          open={openEndScreens}
          onClose={() => setOpenEndScreens(false)}
          existingScreens={existingEndScreens}
          existingLanguages={existingEndLanguages}
          onSave={handleSaveEndScreens}
        />
      )}
      {!isRestrictedSurveyType && (
        <AnalyticsManagerModal
          open={openAnalyticsManager}
          onClose={() => setOpenAnalyticsManager(false)}
          questions={questionDetails?.questions || {}}
          onSave={handleSaveAnalytics}
        />
      )}
      {!isRestrictedSurveyType && (
        <QuestionStructureManagerModal
          open={openStructureManager}
          onClose={() => setOpenStructureManager(false)}
          questions={questionDetails?.questions || {}}
          languages={
            (questionDetails?.questions && questionDetails.questions.language) ||
            advLanguages
          }
          onSave={(updatedQuestions) => {
            try {
              dispatch({
                type: SG_EDIT_SURVEY_QUESTION,
                payload: { id: questionId, questions: updatedQuestions },
              });
            } catch (error) {
              console.error(error);
            }
          }}
        />
      )}
      {!isRestrictedSurveyType && (
        <Dialog open={openOrderManager} onOpenChange={setOpenOrderManager}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Reorder Questions and Set Importance</DialogTitle>
              <DialogDescription>
                Drag items to reorder, then save.
              </DialogDescription>
            </DialogHeader>
          <QuestionOrderManager
            questions={questionDetails?.questions || {}}
            showInternalSave={false}
            onItemsChange={setReorderItems}
              onQuestionsChange={(updatedQuestions) => {
              try {
                dispatch({
                  type: SG_EDIT_SURVEY_QUESTION,
                    payload: { id: questionId, questions: updatedQuestions },
                });
                } catch (error) {
                  console.error(error);
              }
            }}
          />
            <DialogFooter className="pt-4">
              <Button variant="outline" onClick={() => setOpenOrderManager(false)}>
                Cancel
          </Button>
          <Button
            onClick={() => {
              try {
                const current = questionDetails?.questions || {};
                    const updated = applyOrderAndImportance(
                      current,
                      reorderItems
                    );
                dispatch({
                  type: SG_EDIT_SURVEY_QUESTION,
                  payload: { id: questionId, questions: updated },
                });
                  } catch (error) {
                    console.error(error);
              }
              setOpenOrderManager(false);
            }}
          >
            Save
          </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {questionDetails?.survey_type === 10 && (
        <OutcomeStructureManagerModal
          open={openOutcomeStructureManager}
          onClose={() => setOpenOutcomeStructureManager(false)}
          outcomes={
            Array.isArray(questionDetails?.questions)
              ? questionDetails?.questions
              : questionDetails?.questions?.outcomes || []
          }
          languages={
            (questionDetails?.questions && questionDetails.questions.language) ||
            advLanguages
          }
          onSave={(payload) => {
            try {
              const outcomes = Array.isArray(payload)
                ? payload
                : payload?.outcomes || [];
              dispatch({
                type: SG_EDIT_SURVEY_QUESTION,
                payload: { id: questionId, questions: outcomes },
              });
            } catch (error) {
              console.error(error);
            }
          }}
        />
      )}
      {questionDetails?.survey_type === 11 && (
        <OutcomeFeedbackStructureManagerModal
          open={openOutcomeFeedbackStructureManager}
          onClose={() => setOpenOutcomeFeedbackStructureManager(false)}
          feedback={
            Array.isArray(questionDetails?.questions)
              ? questionDetails?.questions
              : questionDetails?.questions?.feedback || []
          }
          languages={
            (questionDetails?.questions && questionDetails.questions.language) ||
            advLanguages
          }
          onSave={(payload) => {
            try {
              const feedback = Array.isArray(payload)
                ? payload
                : payload?.feedback || [];
              dispatch({
                type: SG_EDIT_SURVEY_QUESTION,
                payload: { id: questionId, questions: feedback },
              });
            } catch (error) {
              console.error(error);
            }
          }}
        />
      )}
    </div>
  );
};

export default Details;
